import { Component, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import _ from 'lodash';
import { forkJoin, Observable, ReplaySubject, BehaviorSubject } from 'rxjs';
import {
  InviteUserDialogComponent,
  IUserDialogInput,
  IUserDialogResult,
} from 'src/app/components/invite-user-dialog/invite-user-dialog.component';
import { CheckboxClicked, DeleteClicked, RowActionClicked } from 'src/app/components/table-row/table-row.component';
import {
  ConfirmData,
  ConfirmationDialogComponent,
} from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import {
  AccessGroupApiResponse,
  AccountManagementAuthenticationModel,
  AccountManagementInvitationModel,
  AccountManagementUserModel,
  UsersSupportedActions,
  ZenityUserRoleEnum,
} from 'src/app/models/api/accountManagement.model';
import { MenuAction } from 'src/app/models/menuAction.model';
import { UsersTableColumns } from 'src/app/models/tableColumns/usersColumns.model';
import { UserViewModel } from 'src/app/models/viewModels/userViewModel';
import { AccountManagementHttpService } from 'src/app/services/httpServices/accountManagementHttpService/account-management-http-service.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { UserService } from 'src/app/services/infra/user/user.service';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit {
  public users: AccountManagementUserModel[] = [];
  public invitations: AccountManagementInvitationModel[] = [];
  public authentications: AccountManagementAuthenticationModel[] = [];
  private _accessGroups: AccessGroupApiResponse[] = [];

  public usersCurrentPage$: BehaviorSubject<BehaviorSubject<UserViewModel>[]> = new BehaviorSubject<
    BehaviorSubject<UserViewModel>[]
  >([]);

  public resetSearchBoxBehavioralSubject$: ReplaySubject<undefined> = new ReplaySubject<undefined>();

  public tableRows: Observable<BehaviorSubject<UserViewModel>[]>;
  public usersTableCols = UsersTableColumns;

  // form control - checkbox
  public allRowsSelected = new FormControl(false);
  public checkedUserIds: Set<string> = new Set();
  public checkedUserNames: Set<string> = new Set();

  public multiSelectedMenuActions$: BehaviorSubject<MenuAction[]> = new BehaviorSubject<MenuAction[]>([]);

  public isLoading = signal<boolean>(true);

  private readonly _userRowActions: MenuAction[] = [
    {
      name: UsersSupportedActions.Edit.toString(),
      displayNameLabel: 'userManagementPage.usersPage.actions.edit',
      disabled: false,
      iconPath: 'assets/images/common/edit.svg',
    },
    {
      name: UsersSupportedActions.Delete.toString(),
      displayNameLabel: 'userManagementPage.usersPage.actions.delete',
      disabled: false,
      iconPath: 'assets/images/common/delete.svg',
    },
  ];

  private readonly _invitationRowActions: MenuAction[] = [
    {
      name: UsersSupportedActions.Copy.toString(),
      displayNameLabel: 'userManagementPage.usersPage.actions.copyInvitationUrl',
      disabled: false,
      iconPath: 'assets/images/common/copy.svg',
    },
    {
      name: UsersSupportedActions.Delete.toString(),
      displayNameLabel: 'userManagementPage.usersPage.actions.delete',
      disabled: false,
      iconPath: 'assets/images/common/delete.svg',
    },
  ];

  private _searchedString?: string = '';

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _accountHttpService: AccountManagementHttpService,
    private readonly _permissionsService: PermissionsService,
    private readonly _notificationService: NotificationsService,
    private readonly _userService: UserService
  ) {
    // in order to keep the scope of the function for this. inspiration: https://stackoverflow.com/questions/38245450/angular2-components-this-is-undefined-when-executing-callback-function
    this.deleteUser = this.deleteUser.bind(this);
    this.deleteInvitation = this.deleteInvitation.bind(this);
    this.handleMultipleDeleteAction = this.handleMultipleDeleteAction.bind(this);
  }

  ngOnInit(): void {
    this.isLoading.set(true);
    this._requestForPageData();
  }

  public userSearched(searchedUsers?: string) {
    this._searchedString = searchedUsers;
    this._buildUsersViewModel(searchedUsers);
  }

  public inviteUserClicked() {
    const dialogData: IUserDialogInput = {
      mode: 'invite',
      authentications: this.authentications,
    };
    const dialogRef = this._matDialog.open(InviteUserDialogComponent, {
      data: dialogData,
      disableClose: false,
      panelClass: 'popup-dialog-with-overflow',
    });
    dialogRef.afterClosed().subscribe((result?: IUserDialogResult) => {
      if (result?.success) {
        this._refreshTable();
      }
    });
  }

  public onCheckboxClick(checkbox: CheckboxClicked) {
    const id: string = checkbox.id;
    const status: boolean = checkbox.status;
    const userRow = checkbox.row.value as UserViewModel;

    if (status) {
      this.checkedUserIds.add(id);
      this.checkedUserNames.add(userRow.email);
    } else {
      this.checkedUserIds.delete(id);
      this.checkedUserNames.delete(userRow.email);
      this.allRowsSelected.setValue(false, { emitEvent: false });
    }

    if (this.checkedUserIds.size == this._accountHttpService.defaultPageSize) {
      this.allRowsSelected.setValue(true, { emitEvent: false });
    }
  }

  public deleteUser(ids: string[]): Observable<string> {
    return this._accountHttpService.deleteUsers(ids);
  }

  public deleteInvitation(ids: string[]): Observable<string> {
    return this._accountHttpService.deleteInvitation(ids[0]);
  }

  public deleteIconOnClick(userDetails: DeleteClicked) {
    const userRow = userDetails.moreData as UserViewModel;
    this._openDeleteDialog(userDetails.id, userRow);
  }

  public onRowActionClicked(actionClicked: RowActionClicked) {
    const action = actionClicked.action;
    const userRow = actionClicked.row.value as UserViewModel;

    if (action.name === UsersSupportedActions.Delete.toString()) {
      this._openDeleteDialog(userRow.rowId, userRow);
    } else if (action.name === UsersSupportedActions.Edit.toString()) {
      this._openEditUserDialog(userRow);
    } else if (action.name === UsersSupportedActions.Copy.toString()) {
      this._copyInvitationUrl(userRow.rowId);
    }
  }

  private _openDeleteDialog(id: string, userRow: UserViewModel) {
    const isUser = userRow.isUser;
    const elementName = isUser ? userRow.name : userRow.email;

    const confirmData: ConfirmData = {
      title: 'Remove user',
      content: `Are you sure you want to remove user <strong>${elementName}</strong>?`,
      positiveOptionText: 'Remove',
      negativeOptionText: 'Cancel',
      icon: 'users',
    };

    const dialogRef = this._matDialog.open(ConfirmationDialogComponent, {
      data: confirmData,
      disableClose: false,
      panelClass: 'confirmation-dialog-container',
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const deleteAction = isUser ? this.deleteUser : this.deleteInvitation;
        deleteAction([id]).subscribe({
          next: () => {
            this._notificationService.success(`${elementName} was deleted successfully`);
            this._refreshTable();
          },
          error: () => {
            this._notificationService.error('Failed to delete user');
          },
        });
      }
    });
  }

  private _openEditUserDialog(userRow: UserViewModel) {
    const user = this.users.find((u) => u.userId === userRow.rowId);
    if (!user) {
      return;
    }

    const userAccessGroupIds = this._accessGroups
      .filter((group) => group.users.includes(user.userId) || group.users.includes(user.email))
      .map((group) => group.id);

    const dialogData: IUserDialogInput = {
      mode: 'edit',
      authentications: this.authentications,
      user: {
        userId: user.userId,
        email: user.email,
        name: user.name,
        isAdmin: user.role === ZenityUserRoleEnum.admin,
        accessGroupIds: userAccessGroupIds,
      },
    };

    const dialogRef = this._matDialog.open(InviteUserDialogComponent, {
      data: dialogData,
      disableClose: false,
      panelClass: 'popup-dialog-with-overflow',
    });

    dialogRef.afterClosed().subscribe((result?: IUserDialogResult) => {
      if (result?.success) {
        this._refreshTable();
      }
    });
  }

  private async _copyInvitationUrl(invitationId: string) {
    try {
      const invitation = this.invitations.find((inv) => inv.id === invitationId);
      if (!invitation?.invitationUrl) {
        return;
      }

      await navigator.clipboard.writeText(invitation.invitationUrl);
      this._notificationService.success('Invitation URL copied to clipboard');
    } catch (error) {
      this._notificationService.error('Failed to copy invitation URL');
    }
  }

  public multiSelectedActionClick(menuAction: MenuAction) {
    if (menuAction.name === UsersSupportedActions.Delete.toString()) {
      const count = this.checkedUserIds.size;
      const confirmData: ConfirmData = {
        title: 'Remove users',
        content: `Are you sure you want to remove <strong>${count}</strong> selected user${count > 1 ? 's' : ''}?`,
        positiveOptionText: 'Remove',
        negativeOptionText: 'Cancel',
        icon: 'users',
      };

      const dialogRef = this._matDialog.open(ConfirmationDialogComponent, {
        data: confirmData,
        disableClose: false,
        panelClass: 'confirmation-dialog-container',
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.handleMultipleDeleteAction(this.checkedUserIds).subscribe({
            next: () => {
              this._notificationService.success(`${count} user${count > 1 ? 's were' : ' was'} deleted successfully`);
              this._refreshTable();
            },
            error: () => {
              this._notificationService.error('Failed to delete users');
            },
          });
        }
      });
    }
  }

  public handleMultipleDeleteAction(usersOrInvitationsIds: Set<string>) {
    let userIdsToDelete: string[] = _.map(this.users, 'userId');
    userIdsToDelete = userIdsToDelete.filter((id) => usersOrInvitationsIds.has(id));
    let invitationIdsToDelete: string[] = _.map(this.invitations, 'id');
    invitationIdsToDelete = invitationIdsToDelete.filter((id) => usersOrInvitationsIds.has(id));

    const observables: Observable<string>[] = [];

    for (const invitation of invitationIdsToDelete) {
      observables.push(this._accountHttpService.deleteInvitation(invitation));
    }
    if (userIdsToDelete.length > 0) {
      observables.push(this._accountHttpService.deleteUsers(userIdsToDelete));
    }

    return forkJoin(observables);
  }

  private _refreshTable() {
    this.isLoading.set(true);
    this.checkedUserIds.clear();
    this.checkedUserNames.clear();
    this._requestForPageData();
  }

  private _buildUsersViewModel(searched?: string) {
    const viewModels: BehaviorSubject<UserViewModel>[] = [];

    searched = searched?.toLowerCase();

    this._buildUserViewModelForInvitations(viewModels, searched);

    this._buildUserViewModelForUsers(viewModels, searched);

    this.usersCurrentPage$.next(viewModels);

    this.tableRows = new BehaviorSubject(viewModels);
  }

  private _buildUserViewModelForInvitations(viewModels: BehaviorSubject<UserViewModel>[], searched?: string) {
    this.invitations.forEach((invitation) => {
      if (!searched || invitation.email.toLowerCase().includes(searched)) {
        const viewModel: UserViewModel = {
          rowId: invitation.id,
          email: invitation.email,
          name: '',
          createdAt: invitation.createdAt,
          // It is here because that path is not URL, it raised an error in the console
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          pictureUrl: 'assets/images/common/pending-icon.svg' as any,
          disabled: true,
          clickable: false,
          isUser: false,
          invitationDesc: 'Invitation pending',
          menuActions: this._invitationRowActions,
        } as UserViewModel;

        viewModels.push(new BehaviorSubject(viewModel));
      }
    });
  }

  private _buildUserViewModelForUsers(viewModels: BehaviorSubject<UserViewModel>[], searched?: string) {
    this.users.forEach((user) => {
      if (!searched || user.email.toLowerCase().includes(searched) || user.name.toLowerCase().includes(searched)) {
        const isCurrentUser = this._userService.loggedInUser?.email === user.email;
        const userGroupNames = this._getUserAccessGroupNames(user.userId, user.email);
        const viewModel: UserViewModel = {
          rowId: user.userId,
          email: user.email,
          name: user.name,
          createdAt: user.createdAt,
          lastLogin: user.lastLogin?.toString() ? new Date(user.lastLogin) : undefined,
          pictureUrl: user.pictureUrl,
          clickable: false,
          isUser: true,
          isStrictMode: isCurrentUser,
          isAdmin: user.role === ZenityUserRoleEnum.admin,
          memberOfGroups: user.role === ZenityUserRoleEnum.admin ? ['Admin'] : userGroupNames,
          menuActions: isCurrentUser ? [] : this._userRowActions,
        } as UserViewModel;

        viewModels.push(new BehaviorSubject(viewModel));
      }
    });
  }

  private _getUserAccessGroupNames(userId: string, email: string): string[] {
    return this._accessGroups
      .filter((group) => group.users.includes(userId) || group.users.includes(email))
      .map((group) => group.name);
  }

  private _onAllRowsSelectionClick(newValue: boolean | null) {
    const checkboxStatus = newValue ? true : false;

    this.usersCurrentPage$.value.forEach((userRow$) => {
      const userRow = userRow$.value;
      userRow.checkboxStatus = checkboxStatus;
      userRow$.next(userRow);
      if (checkboxStatus === true) {
        this.checkedUserIds.add(userRow.rowId);
        this.checkedUserNames.add(userRow.email);
      } else {
        this.checkedUserIds.delete(userRow.rowId);
        this.checkedUserNames.delete(userRow.email);
      }
    });
  }

  private _requestForPageData() {
    forkJoin([
      this._accountHttpService.getUsers(),
      this._accountHttpService.getInvitations(),
      this._accountHttpService.getAuthentications(),
      this._accountHttpService.getAccessGroups(),
    ]).subscribe((observablesResults) => {
      this.users = observablesResults[0];
      this.invitations = observablesResults[1];
      this.authentications = observablesResults[2];
      this._accessGroups = observablesResults[3]._embedded?.items || [];

      this.allRowsSelected.valueChanges.subscribe((value) => this._onAllRowsSelectionClick(value));
      this._updateMultiSelectActions();

      this.isLoading.set(false);
      this._buildUsersViewModel();
    });
  }

  private _updateMultiSelectActions() {
    this.multiSelectedMenuActions$.next([
      {
        name: UsersSupportedActions.Delete.toString(),
        displayNameLabel: 'userManagementPage.usersPage.deleteUsersMultiActionName',
        iconPath: 'assets/images/common/delete.svg',
        disabled: !this._permissionsService.isAdmin(),
      } as MenuAction,
    ]);
  }
}
